import React, { useState, useEffect, useCallback } from 'react';
import { useField } from 'formik';
import PropTypes from 'prop-types';

/**
 * Fast textarea component with local state and debouncing for better performance.
 * This component maintains a local state for the textarea value and debounces
 * updates to Formik to prevent performance issues with large forms.
 */
const FastFormikTextarea = ({ name, initialValue, debounceDelay = 300, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const [localValue, setLocalValue] = useState(initialValue || field.value || '');

  // Update local state when field value changes
  useEffect(() => {
    setLocalValue(field.value || '');
  }, [field.value]);

  // Handle local value change
  const handleChange = useCallback(e => {
    setLocalValue(e.target.value);
  }, []);

  // Debounced update to Formik
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (localValue !== field.value) {
        helpers.setValue(localValue);
      }
    }, debounceDelay);

    return () => clearTimeout(timeout);
  }, [localValue, field.value, helpers, debounceDelay]);

  return (
    <textarea
      {...props}
      name={name}
      value={localValue}
      onChange={handleChange}
      onBlur={() => {
        // Immediately update on blur
        helpers.setValue(localValue);
        helpers.setTouched(true);
      }}
    />
  );
};

FastFormikTextarea.propTypes = {
  name: PropTypes.string.isRequired,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  debounceDelay: PropTypes.number,
};

export default FastFormikTextarea;